import { useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useCreateComment } from '@/lib/hooks/useComments';
import { useAuth } from '@/lib/hooks/useAuth';

type AddCommentDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: number;
};

export function AddCommentDrawer({
  open,
  onOpenChange,
  postId,
}: AddCommentDrawerProps) {
  const [content, setContent] = useState('');
  const { user } = useAuth();
  const createComment = useCreateComment();

  const handleSubmit = async () => {
    if (!content.trim() || !user?.id) {
      return;
    }

    try {
      await createComment.mutateAsync({
        postId,
        userId: user.id,
        content: content.trim(),
      });
      // Reset form and close drawer on success
      setContent('');
      onOpenChange(false);
    } catch (error) {
      // Error handling is done by TanStack Query
      console.error('Failed to create comment:', error);
    }
  };

  const isSubmitting = createComment.isPending;
  const canSubmit = content.trim().length > 0 && !isSubmitting && !!user?.id;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Add Comment</DrawerTitle>
        </DrawerHeader>
        <div className="py-4">
          <Textarea
            placeholder="Write a comment..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={isSubmitting}
            className="min-h-[120px] resize-none"
            autoFocus
          />
        </div>
        <DrawerFooter>
          <div className="flex gap-3">
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex-1 h-14"
            >
              {isSubmitting ? 'Posting...' : 'Post'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}


